import { infiniteQueryOptions, keepPreviousData, queryOptions, useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query'
import { campaignKeys } from '@/features/campaigns/api'
import { displayKeys } from '@/features/displays/api'
import { api, unwrap } from '@/lib/api'
import type { DataEnvelope, Paginated, Schedule, ScheduleCalendar, ScheduleConflict, ScheduleTargetType, Simulation } from '@/types/api'
import type { ScheduleInput } from './lib/schedule-form'

export interface ScheduleListParams {
  search?: string
  campaign_id?: string
  target_type?: ScheduleTargetType
  target_id?: string
}

export interface CalendarParams {
  from: string
  to: string
  display_id?: string
  location_id?: string
  campaign_id?: string
}

export const scheduleKeys = {
  all: ['schedules'] as const,
  lists: () => [...scheduleKeys.all, 'list'] as const,
  list: (params: ScheduleListParams) => [...scheduleKeys.lists(), params] as const,
  details: () => [...scheduleKeys.all, 'detail'] as const,
  detail: (id: string) => [...scheduleKeys.details(), id] as const,
}

export const calendarKeys = {
  all: ['calendar'] as const,
  range: (params: CalendarParams) => [...calendarKeys.all, params] as const,
}

export const insightKeys = {
  simulate: (displayId: string, at: string | null) => [...displayKeys.detail(displayId), 'simulate', at ?? 'now'] as const,
  conflicts: (displayId: string, from: string, to: string) => [...displayKeys.detail(displayId), 'conflicts', from, to] as const,
}

/* ------------------------------------------------------------------ queries */

export function schedulesInfiniteOptions(params: ScheduleListParams) {
  return infiniteQueryOptions({
    queryKey: scheduleKeys.list(params),
    queryFn: ({ pageParam, signal }) => api.get<Paginated<Schedule>>('/schedules', { signal, query: { ...params, cursor: pageParam, per_page: 50 } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.meta?.next_cursor ?? undefined,
  })
}

export function useSchedules(params: ScheduleListParams, enabled = true) {
  return useInfiniteQuery({ ...schedulesInfiniteOptions(params), enabled })
}

export function scheduleOptions(id: string) {
  return queryOptions({
    queryKey: scheduleKeys.detail(id),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<Schedule>>(`/schedules/${id}`, { signal })),
  })
}

export function useSchedule(id: string | null | undefined) {
  return useQuery({ ...scheduleOptions(id ?? ''), enabled: !!id })
}

export function useCalendar(params: CalendarParams | null) {
  return useQuery({
    queryKey: calendarKeys.range(params ?? { from: '', to: '' }),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<ScheduleCalendar>>('/calendar', { signal, query: { ...params } })),
    enabled: !!params,
    placeholderData: keepPreviousData,
    // Occurrence status (past / active) moves with the clock.
    refetchInterval: 60_000,
  })
}

/** `at` = ISO instant, or null for "now". */
export function useSimulation(displayId: string, at: string | null) {
  return useQuery({
    queryKey: insightKeys.simulate(displayId, at),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<Simulation>>(`/displays/${displayId}/simulate`, { signal, query: { at } })),
    placeholderData: keepPreviousData,
    staleTime: 0,
  })
}

export function useConflicts(displayId: string, from: string, to: string) {
  return useQuery({
    queryKey: insightKeys.conflicts(displayId, from, to),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<ScheduleConflict[]>>(`/displays/${displayId}/conflicts`, { signal, query: { from, to } })),
  })
}

/* ---------------------------------------------------------------- caching */

/** Any schedule write recomputes displays' local schedules: refresh lists, calendars and per-display insights. */
export function invalidateScheduleViews(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: scheduleKeys.lists() })
  void qc.invalidateQueries({ queryKey: calendarKeys.all })
  void qc.invalidateQueries({ queryKey: displayKeys.details() })
  void qc.invalidateQueries({ queryKey: campaignKeys.all })
  void qc.invalidateQueries({ queryKey: ['dashboard'] })
  void qc.invalidateQueries({ queryKey: ['onboarding'] })
}

function patchScheduleCache(qc: QueryClient, schedule: Schedule) {
  qc.setQueryData(scheduleKeys.detail(schedule.id), schedule)
  qc.setQueriesData<InfiniteData<Paginated<Schedule>>>({ queryKey: scheduleKeys.lists() }, (data) =>
    data?.pages ? { ...data, pages: data.pages.map((p) => ({ ...p, data: p.data.map((s) => (s.id === schedule.id ? schedule : s)) })) } : data,
  )
}

/* ---------------------------------------------------------------- mutations */

export function useCreateSchedule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: ScheduleInput) => unwrap(api.post<DataEnvelope<Schedule>>('/schedules', input)),
    onSuccess: (s) => {
      qc.setQueryData(scheduleKeys.detail(s.id), s)
      invalidateScheduleViews(qc)
    },
  })
}

export function useUpdateSchedule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<ScheduleInput> & { id: string }) => unwrap(api.patch<DataEnvelope<Schedule>>(`/schedules/${id}`, input)),
    onSuccess: (s) => {
      patchScheduleCache(qc, s)
      invalidateScheduleViews(qc)
    },
  })
}

export function useDeleteSchedule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/schedules/${id}`),
    onSuccess: (_d, id) => {
      qc.setQueriesData<InfiniteData<Paginated<Schedule>>>({ queryKey: scheduleKeys.lists() }, (data) =>
        data?.pages ? { ...data, pages: data.pages.map((p) => ({ ...p, data: p.data.filter((s) => s.id !== id) })) } : data,
      )
      qc.removeQueries({ queryKey: scheduleKeys.detail(id) })
      invalidateScheduleViews(qc)
    },
  })
}
